<template>
  <div class="theme--light">
    <div class="form-group">
      <label
        for="dropdown-select"
        aria-label="Select something"
        class="form-label">
        {{ label }}
        <span v-if="$props.required && $props.label" class="required"></span>
        <span v-if="optionalLabel" class="optional-label">
          ({{ optionalLabel }})
        </span>
      </label>
      <v-select
        :placeholder="placeholder"
        :class="[
          `dropdown-select`,
          error && `dropdown-select--error`,
          hover && `dropdown-select--hover`,
        ]"
        :multiple="multiple"
        :disabled="disabled"
        :close-on-select="closeOnSelect"
        :options="options"
        :filterable="false"
        :value="value"
        :clearable="false"
        :selectable="(option) => option.value !== 'Select'"
        :loading="loading"
        @search="onSearch"
        @input="setSelected">
        <template #search="{ attributes, events }">
          <input
            class="dropdown-select__input vs__search"
            v-bind="attributes"
            v-on="events" />
        </template>
        <template #selected-option-container="{ option, deselect }">
          <div
            class="vs__selected dropdown-select__values"
            aria-label="dropdown container">
            <span
              :class="[
                'dropdown-select__values__content',
                { 'dropdown-select__values__content--multiple': multiple },
              ]">
              {{ option.value }}
            </span>
            <button
              type="button"
              title="deselect"
              aria-label="deselect"
              class="dropdown-select__values__button"
              @click="deselect(option)">
              <svg
                v-if="multiple"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.29293 5.00001L0.646484 8.64645L1.35359 9.35356L5.00004 5.70711L8.64648 9.35356L9.35359 8.64645L5.70715 5.00001L9.35359 1.35356L8.64648 0.646454L5.00004 4.2929L1.35359 0.646454L0.646484 1.35356L4.29293 5.00001Z"
                  fill="#878787" />
              </svg>
            </button>
          </div>
        </template>
        <template #open-indicator="{ attributes }">
          <icon
            v-bind="attributes"
            class-name="dropdown-select__dropdown-icon"
            name="chevron-down" />
        </template>
        <template #spinner="{ loading }">
          <div
            v-if="loading"
            style="border-left-color: #42b0d5"
            class="vs__spinner"
            aria-label="loading spinner"></div>
        </template>
        <template #no-options="{ searching }">
          <template v-if="searching">
            <em class="dropdown-select__no-results">No results found</em>
          </template>
          <em v-else class="dropdown-select__no-options">No Options</em>
        </template>
      </v-select>
      <p
        v-if="errorMessage"
        class="form-label form-label--error form-label--error--active"
        aria-label="Error Message">
        {{ errorMessage }}
      </p>
      <div
        v-if="helpMessage"
        :class="[`form-help`, helpMessagePosition === `right` && `text-right`]"
        aria-label="Help Message">
        {{ helpMessage }}
      </div>
    </div>
  </div>
</template>

<script>
// Importing Vue and Vue Select
import vSelect from "vue-select";
// Importing the Vue Select Styles
import "vue-select/dist/vue-select.css";
export default {
  name: "Dropdown",
  components: {
    vSelect,
  },
  props: {
    /**
     * @description
     * Provides the list of Select Options for Select Input
     */
    options: {
      type: Array,
      required: true,
    },
    /**
     * @description
     * Provides Label for Select Input
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Placeholder Text for Select Input
     */
    placeholder: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Optional Label for Select Input
     */
    optionalLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Filter Name to the Dropdown in case it is being used with Filters
     * Useful to Sync Filter Configurations
     */
    filterName: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Dropdown Label for the Vue Select to Display the Label Names for the Options
     */
    dropdownLabel: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Error Message for Select Input
     */
    errorMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Provides Help Message for Select Input
     */
    helpMessage: {
      type: String,
      default: "",
    },
    /**
     * @description
     * Checks the Select Input help message position
     * Maersk Design System supports left and right
     */
    helpMessagePosition: {
      type: String,
      validator: (val) => ["left", "right"].includes(val),
      default: "left",
    },
    /**
     * @description
     * Emits the value change on input, Parent Component needs to sync values emitted
     */
    value: {
      type: [String, Object, Array],
      required: true,
    },
    /**
     * @description
     * Can the user clear the selected property?
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Flag to enable/disable loading state of the dropdown
     * If we are using APIs to filter data then this should be enabled/disabled while making APIs Requests
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Flag to enable/disable UI Filter of the Dropdown options
     * When loading server side options, it can be useful to disable the client side filtering
     */
    filterable: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Close a dropdown when an option is chosen.
     * Set to false to keep the dropdown open (useful when combined with multi-select)
     */
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    /**
     * @description
     * Provides Type of Select Input
     * Maersk Design System supports Default & Multiple Select
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be Disabled
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should be Hoverable
     */
    hover: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should display Error Outline
     */
    error: {
      type: Boolean,
      default: false,
    },
    /**
     * @description
     * Checks if the Select Input should display as Required
     */
    required: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    /**
     * @description
     * The search event provides a hook to load options from a parent component when the search text is updated
     * Emits the Filter Search when user types 3 or more characters
     */
    onSearch(search) {
      this.$emit("onSearch", {
        type: "dropdown",
        filterName: this.filterName,
        searchValue: search,
      });
    },
    /**
     * @description
     * Emits the Selected Value along with the Filter Name (if any)
     */
    setSelected(value) {
      this.$emit("onChange", {
        type: "dropdown",
        filterName: this.filterName,
        value: value,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-select {
  & .vs__dropdown-toggle {
    min-height: 43px;
    padding-left: 12px;
    padding-right: 12px;
    &:hover {
      border: 1px solid var(--Gray-600);
    }
    & .dropdown-select__values {
      padding: 0.25rem;
      padding-left: 0.75rem;
    }
  }
  &--error {
    & .vs__dropdown-toggle {
      border: 1px solid var(--Danger) !important;
      box-shadow: 0 0 0 3px var(--Danger-Light) !important;
    }
  }
  &--hover {
    & .vs__dropdown-toggle {
      border: 1px solid var(--Gray-600);
    }
  }
  &__values {
    &__button {
      background-color: Transparent;
      background-repeat: no-repeat;
      border: none;
      cursor: pointer;
      overflow: hidden;
    }
  }
  .required:after {
    content: " *";
    color: red;
  }
}
.vs--open {
  & .vs__dropdown-toggle {
    border: 1px solid var(--Blue-400) !important;
    box-shadow: 0 0 0 3px var(--Info-Light) !important;
  }
}
</style>
